import {
  Alert,
  Badge,
  Box,
  Button,
  Group,
  Modal,
  Paper,
  ScrollArea,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconAlertCircle,
  IconEye,
  IconFileExport,
} from '@tabler/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '@/store/appStore';
import { usePlaeneList } from '@/api/usePlaene';
import { apiClient } from '@/auth/apiClient';
import { LoadingState } from '@/components/LoadingState';
import { ErrorState } from '@/components/ErrorState';

type ExportFormat = 'xlsx' | 'pdf' | 'csv';

interface PersonnelRow {
  name: string;
  kategorie: string;
  eg_gruppe: string;
  eg_stufe: string;
  std_woche: number;
  fachkraft: boolean;
  sue_zulage: boolean;
  bruttos: number[];
  summe: number;
}

interface SachkostenTableRow {
  art: string;
  modus: string;
  betrag_monatlich: number;
  aktiv_ab_monat: number | null;
  aktiv_bis_monat: number | null;
  monate: number[];
  summe: number;
}

interface EinnahmenTableRow {
  quelle: string;
  bezeichnung: string;
  verteilung: string;
  monate: number[];
  summe: number;
}

interface PreviewPayload {
  plan: { id: string; name: string; foerderjahr: number; status: string };
  personnel: {
    months: string[];
    rows: PersonnelRow[];
    engine_available: boolean;
  };
  sachkosten: {
    months: string[];
    rows: SachkostenTableRow[];
  };
  einnahmen: {
    months: string[];
    rows: EinnahmenTableRow[];
  };
  snapshot: {
    available: boolean;
    engine_version?: string;
    created_at?: string;
    personalkosten_jahr?: number;
    sachkosten_jahr?: number;
    einnahmen_jahr?: number;
    ergebnis_jahr?: number;
    saldo_jahr?: number;
    deckungsluecke_jahr?: number;
    kinder_durchschnitt?: number;
    personalbedarf_jahr_std?: number;
  };
}

const MONAT_KURZ = [
  'Jan', 'Feb', 'Mrz', 'Apr', 'Mai', 'Jun',
  'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez',
];

function formatAktiv(ab: number | null, bis: number | null): string {
  if (ab == null && bis == null) return 'ganzjährig';
  const von = ab != null ? MONAT_KURZ[ab - 1] : 'Jan';
  const ende = bis != null ? MONAT_KURZ[bis - 1] : 'Dez';
  return `${von} – ${ende}`;
}

function fmtEur(v: number | undefined, lang: string): string {
  if (v === undefined || v === null) return '—';
  return new Intl.NumberFormat(lang, {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(v);
}

function fmtEur2(v: number, lang: string): string {
  return new Intl.NumberFormat(lang, {
    style: 'currency',
    currency: 'EUR',
  }).format(v);
}

export function ExportPage() {
  const { t, i18n } = useTranslation(['common']);
  const lang = i18n.language;
  const einrichtungId = useAppStore((s) => s.activeEinrichtungId);
  const jahr = useAppStore((s) => s.activeFoerderjahr);
  const plaeneQuery = usePlaeneList({
    einrichtung_id: einrichtungId ?? undefined,
    foerderjahr: jahr,
  });
  const [planId, setPlanId] = useState<string | null>(null);
  const [busy, setBusy] = useState<ExportFormat | null>(null);
  const [previewOpened, preview] = useDisclosure(false);
  const [previewData, setPreviewData] = useState<PreviewPayload | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const handlePreview = async () => {
    if (!planId) return;
    setPreviewLoading(true);
    setPreviewError(null);
    setPreviewData(null);
    preview.open();
    try {
      const res = await apiClient.get<PreviewPayload>(
        `/plan/${planId}/export/preview`,
      );
      setPreviewData(res.data);
    } catch (e) {
      const err = e as { message?: string };
      setPreviewError(err.message ?? 'Fehler beim Laden der Vorschau');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleExport = async (fmt: ExportFormat) => {
    if (!planId) return;
    setBusy(fmt);
    try {
      const ext = fmt.toLowerCase();
      const res = await apiClient.get<Blob>(`/plan/${planId}/export.${ext}`, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `plan-${planId}.${ext}`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setBusy(null);
    }
  };

  return (
    <Stack>
      <Title order={2}>{t('common:nav.export')}</Title>
      <Paper withBorder p="md">
        {plaeneQuery.isLoading ? <LoadingState height={120} /> : null}
        {plaeneQuery.isError ? (
          <ErrorState error={plaeneQuery.error} onRetry={() => void plaeneQuery.refetch()} />
        ) : null}
        {plaeneQuery.data ? (
          <Stack>
            <Select
              label={t('common:nav.plaene')}
              data={plaeneQuery.data.map((p) => ({
                value: p.id,
                label: `${p.name} (${p.foerderjahr})`,
              }))}
              value={planId}
              onChange={setPlanId}
              searchable
            />
            <Group>
              <Button
                leftSection={<IconEye size={16} />}
                onClick={() => void handlePreview()}
                disabled={!planId}
                variant="light"
                color="lag"
              >
                Vorschau
              </Button>
              <Button
                leftSection={<IconFileExport size={16} />}
                onClick={() => void handleExport('xlsx')}
                disabled={!planId}
                loading={busy === 'xlsx'}
              >
                XLSX
              </Button>
              <Button
                leftSection={<IconFileExport size={16} />}
                onClick={() => void handleExport('pdf')}
                disabled={!planId}
                loading={busy === 'pdf'}
                variant="default"
              >
                PDF
              </Button>
              <Button
                leftSection={<IconFileExport size={16} />}
                onClick={() => void handleExport('csv')}
                disabled={!planId}
                loading={busy === 'csv'}
                variant="default"
              >
                CSV
              </Button>
            </Group>
          </Stack>
        ) : null}
      </Paper>

      <Modal
        opened={previewOpened}
        onClose={preview.close}
        title="Export-Vorschau"
        size="90%"
        centered
      >
        {previewLoading ? <LoadingState height={200} /> : null}
        {previewError ? (
          <Alert color="red" icon={<IconAlertCircle size={16} />}>
            {previewError}
          </Alert>
        ) : null}
        {previewData ? (
          <Stack gap="md">
            <Box>
              <Title order={4} c="#072c5e">
                {previewData.plan.name}
              </Title>
              <Group gap="xs">
                <Badge variant="light">Förderjahr {previewData.plan.foerderjahr}</Badge>
                <Badge variant="light" color="gray">
                  {previewData.plan.status}
                </Badge>
                {previewData.personnel.engine_available ? null : (
                  <Badge color="orange" variant="light">
                    Engine n/v — Bruttos = 0
                  </Badge>
                )}
              </Group>
            </Box>

            {previewData.snapshot.available ? (
              <Paper withBorder p="sm">
                <Text fw={600} mb="xs" c="#072c5e">
                  Snapshot-Summary
                </Text>
                <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }} spacing="xs">
                  <Stat label="Personalkosten" v={previewData.snapshot.personalkosten_jahr} lang={lang} />
                  <Stat label="Sachkosten" v={previewData.snapshot.sachkosten_jahr} lang={lang} />
                  <Stat label="Einnahmen" v={previewData.snapshot.einnahmen_jahr} lang={lang} />
                  <Stat label="Ergebnis" v={previewData.snapshot.ergebnis_jahr ?? previewData.snapshot.saldo_jahr} lang={lang} />
                  <Stat label="Deckungslücke" v={previewData.snapshot.deckungsluecke_jahr} lang={lang} />
                  <Stat
                    label="Kinder ⌀"
                    raw={previewData.snapshot.kinder_durchschnitt?.toString()}
                  />
                  <Stat
                    label="Pers.bedarf Std/J"
                    raw={previewData.snapshot.personalbedarf_jahr_std?.toString()}
                  />
                </SimpleGrid>
              </Paper>
            ) : (
              <Alert color="yellow" icon={<IconAlertCircle size={16} />}>
                Kein Snapshot vorhanden — bitte vorher Plan neu berechnen.
              </Alert>
            )}

            <Paper withBorder p="sm">
              <Text fw={600} mb="xs" c="#072c5e">
                Personalkosten — Brutto je Mitarbeitendem und Monat
              </Text>
              {previewData.personnel.rows.length === 0 ? (
                <Text c="dimmed" size="sm">
                  Keine Personalpositionen im Plan.
                </Text>
              ) : (
                <ScrollArea h={420} type="auto">
                  <Table striped withTableBorder withColumnBorders stickyHeader>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Name</Table.Th>
                        <Table.Th>EG/Stufe</Table.Th>
                        <Table.Th>Std/Wo</Table.Th>
                        {previewData.personnel.months.map((m) => (
                          <Table.Th key={m} style={{ textAlign: 'right' }}>
                            {m}
                          </Table.Th>
                        ))}
                        <Table.Th style={{ textAlign: 'right' }}>Σ Jahr</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {previewData.personnel.rows.map((r, idx) => (
                        <Table.Tr key={idx}>
                          <Table.Td>
                            <Box>
                              <Text size="sm" fw={500}>{r.name}</Text>
                              <Text size="xs" c="dimmed">
                                {r.kategorie}
                              </Text>
                            </Box>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">
                              {r.eg_gruppe} / {r.eg_stufe}
                            </Text>
                          </Table.Td>
                          <Table.Td style={{ textAlign: 'right' }}>
                            {r.std_woche}
                          </Table.Td>
                          {r.bruttos.map((b, mi) => (
                            <Table.Td key={mi} style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                              {fmtEur(b, lang)}
                            </Table.Td>
                          ))}
                          <Table.Td style={{ textAlign: 'right', fontWeight: 600, whiteSpace: 'nowrap' }}>
                            {fmtEur2(r.summe, lang)}
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </ScrollArea>
              )}
            </Paper>

            <Paper withBorder p="sm">
              <Text fw={600} mb="xs" c="#072c5e">
                Sachkosten — Betrag je Position und Monat
              </Text>
              {previewData.sachkosten.rows.length === 0 ? (
                <Text c="dimmed" size="sm">
                  Keine Sachkostenpositionen im Plan.
                </Text>
              ) : (
                <ScrollArea h={300} type="auto">
                  <Table striped withTableBorder withColumnBorders stickyHeader>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Art</Table.Th>
                        <Table.Th>Modus</Table.Th>
                        <Table.Th>Aktiv</Table.Th>
                        {previewData.sachkosten.months.map((m) => (
                          <Table.Th key={m} style={{ textAlign: 'right' }}>
                            {m}
                          </Table.Th>
                        ))}
                        <Table.Th style={{ textAlign: 'right' }}>Σ Jahr</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {previewData.sachkosten.rows.map((r, idx) => (
                        <Table.Tr key={idx}>
                          <Table.Td>{r.art}</Table.Td>
                          <Table.Td>
                            <Text size="xs" c="dimmed">
                              {r.modus}
                            </Text>
                          </Table.Td>
                          <Table.Td style={{ whiteSpace: 'nowrap' }}>
                            {formatAktiv(r.aktiv_ab_monat, r.aktiv_bis_monat)}
                          </Table.Td>
                          {r.monate.map((b, mi) => (
                            <Table.Td key={mi} style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                              {fmtEur(b, lang)}
                            </Table.Td>
                          ))}
                          <Table.Td style={{ textAlign: 'right', fontWeight: 600, whiteSpace: 'nowrap' }}>
                            {fmtEur2(r.summe, lang)}
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </ScrollArea>
              )}
            </Paper>

            <Paper withBorder p="sm">
              <Text fw={600} mb="xs" c="#072c5e">
                Einnahmen — pro Posten und Monat
              </Text>
              {previewData.einnahmen.rows.length === 0 ? (
                <Text c="dimmed" size="sm">
                  Keine Einnahmen-Positionen im Plan.
                </Text>
              ) : (
                <ScrollArea h={300} type="auto">
                  <Table striped withTableBorder withColumnBorders stickyHeader>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Quelle</Table.Th>
                        <Table.Th>Bezeichnung</Table.Th>
                        <Table.Th>Verteilung</Table.Th>
                        {previewData.einnahmen.months.map((m) => (
                          <Table.Th key={m} style={{ textAlign: 'right' }}>
                            {m}
                          </Table.Th>
                        ))}
                        <Table.Th style={{ textAlign: 'right' }}>Σ Jahr</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {previewData.einnahmen.rows.map((r, idx) => (
                        <Table.Tr key={idx}>
                          <Table.Td>
                            <Badge size="xs" variant="light" color="lag">
                              {r.quelle}
                            </Badge>
                          </Table.Td>
                          <Table.Td>{r.bezeichnung}</Table.Td>
                          <Table.Td>
                            <Text size="xs" c="dimmed">
                              {r.verteilung}
                            </Text>
                          </Table.Td>
                          {r.monate.map((b, mi) => (
                            <Table.Td key={mi} style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                              {fmtEur(b, lang)}
                            </Table.Td>
                          ))}
                          <Table.Td style={{ textAlign: 'right', fontWeight: 600, whiteSpace: 'nowrap' }}>
                            {fmtEur2(r.summe, lang)}
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </ScrollArea>
              )}
            </Paper>

            <Group justify="flex-end">
              <Button variant="default" onClick={preview.close}>
                Schließen
              </Button>
              <Button
                leftSection={<IconFileExport size={16} />}
                onClick={() => void handleExport('xlsx')}
                loading={busy === 'xlsx'}
              >
                Als XLSX herunterladen
              </Button>
              <Button
                leftSection={<IconFileExport size={16} />}
                onClick={() => void handleExport('pdf')}
                loading={busy === 'pdf'}
                variant="default"
              >
                Als PDF herunterladen
              </Button>
            </Group>
          </Stack>
        ) : null}
      </Modal>
    </Stack>
  );
}

function Stat({
  label,
  v,
  raw,
  lang,
}: {
  label: string;
  v?: number;
  raw?: string;
  lang?: string;
}) {
  return (
    <Box>
      <Text size="xs" c="dimmed">
        {label}
      </Text>
      <Text size="sm" fw={600}>
        {raw !== undefined && raw !== null
          ? raw
          : v !== undefined && v !== null && lang
            ? fmtEur(v, lang)
            : '—'}
      </Text>
    </Box>
  );
}
